/**
 * Marker trend chart — uPlot-backed line chart with:
 *   - reference-band shading (ref_low/ref_high)
 *   - colored dots for out-of-range points (red/blue/primary)
 *   - event flags on x-axis (meds, notes, illnesses) rendered below chart
 *   - keyboard navigation (Left/Right move focus across points)
 *   - role="img" + aria-label with textual summary
 *   - respects `prefers-reduced-motion` (uPlot has no entrance animation)
 *
 * Wrapper handles lifecycle and resize; uPlot handles the heavy lifting.
 */

import type uPlot from 'uplot';

import { h } from '@/lib/dom';
import { loadUPlot } from '@/lib/uplot-lazy';
import { formatDate, formatDelta, formatLabValue, parseDate, plural } from '@/lib/format';
import type { EventOut, MarkerCatalogEntry, TrendPoint } from '@/api/types';
import { registerCleanup } from '@/router';
import { eventIcon } from './EventFlag';

export interface TrendChartProps {
  marker: MarkerCatalogEntry;
  points: TrendPoint[];
  events?: EventOut[];
  unit?: string;
  referenceRange?: { low: number | null; high: number | null };
  onPointSelect?: (point: TrendPoint) => void;
}

function computeAriaLabel(props: TrendChartProps): string {
  const { marker, points, referenceRange } = props;
  const name = marker.display_ru;
  const n = points.length;
  if (n === 0) return `${name}, нет данных.`;
  const last = points[n - 1]!;
  const value = last.value != null ? `${formatLabValue(last.value)} ${last.unit}` : last.qualitative_value ?? '—';
  const rangeText = referenceRange?.low != null && referenceRange?.high != null
    ? `норма ${formatLabValue(referenceRange.low)}–${formatLabValue(referenceRange.high)}`
    : '';
  const flagText = last.flag === 'H' || last.flag === 'HH' ? 'выше нормы'
    : last.flag === 'L' || last.flag === 'LL' ? 'ниже нормы' : '';
  return `Тренд ${name} за ${n} измерений. Текущее значение ${value}. ${flagText}. ${rangeText}.`;
}

function tooltipText(p: TrendPoint, prev: TrendPoint | null): string {
  const date = formatDate(p.drawn_at);
  const value = p.value != null ? `${formatLabValue(p.value)} ${p.unit}` : p.qualitative_value ?? '—';
  const status = p.flag === 'H' || p.flag === 'HH' ? 'Выше нормы'
    : p.flag === 'L' || p.flag === 'LL' ? 'Ниже нормы' : 'В норме';
  const delta = prev && prev.value != null && p.value != null ? formatDelta(p.value - prev.value, formatLabValue) : '';
  return `${date} · ${value}${delta ? ` · ${delta} vs пред.` : ''} · ${status}`;
}

function pointColor(p: TrendPoint): string {
  if (p.flag === 'H' || p.flag === 'HH') return getVar('--chart-point-above');
  if (p.flag === 'L' || p.flag === 'LL') return getVar('--chart-point-below');
  return getVar('--chart-point-default');
}

function getVar(name: string): string {
  if (typeof getComputedStyle === 'undefined') return '#1564C0';
  const value = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return value || '#1564C0';
}

/**
 * Draw a shaded reference band (rectangle spanning ref_low..ref_high across
 * the full x-axis). We hook it via uPlot's `hooks.drawAxes` so it paints
 * below the series lines.
 */
function bandHook(range?: { low: number | null; high: number | null }): (u: uPlot) => void {
  return (u: uPlot) => {
    if (!range || range.low == null || range.high == null) return;
    const ctx = u.ctx;
    const left = u.bbox.left;
    const width = u.bbox.width;
    const yTop = u.valToPos(range.high, 'y', true);
    const yBottom = u.valToPos(range.low, 'y', true);
    ctx.save();
    ctx.fillStyle = getVar('--chart-reference-band');
    ctx.fillRect(left, yTop, width, yBottom - yTop);
    ctx.strokeStyle = getVar('--chart-reference-border');
    ctx.setLineDash([4, 3]);
    ctx.beginPath();
    ctx.moveTo(left, yTop); ctx.lineTo(left + width, yTop);
    ctx.moveTo(left, yBottom); ctx.lineTo(left + width, yBottom);
    ctx.stroke();
    ctx.restore();
  };
}

/**
 * Draw colored dots for each datum; uPlot's built-in `points` is uniform, but
 * we want per-point colors (H/L/norm). Hook into `drawSeries`.
 */
function pointsHook(points: TrendPoint[]): (u: uPlot) => void {
  return (u: uPlot) => {
    const ctx = u.ctx;
    ctx.save();
    const xs = u.data[0]!;
    for (let i = 0; i < xs.length; i++) {
      const p = points[i]!;
      if (p.value == null) continue;
      const x = u.valToPos(xs[i] as number, 'x', true);
      const y = u.valToPos(p.value, 'y', true);
      ctx.beginPath();
      ctx.arc(x, y, 4, 0, 2 * Math.PI);
      const filled = p.flag === 'H' || p.flag === 'HH' || p.flag === 'L' || p.flag === 'LL';
      ctx.strokeStyle = pointColor(p);
      ctx.lineWidth = 2;
      if (filled) {
        ctx.fillStyle = pointColor(p);
        ctx.fill();
      } else {
        ctx.fillStyle = getVar('--bg-surface');
        ctx.fill();
      }
      ctx.stroke();
    }
    ctx.restore();
  };
}

export function TrendChart(props: TrendChartProps): HTMLElement {
  const aria = computeAriaLabel(props);
  const unit = props.unit ?? props.marker.canonical_unit;
  const range = props.referenceRange ?? {
    low: props.marker.ref_low_default,
    high: props.marker.ref_high_default,
  };

  const chartArea = h('div', {
    class: 'chart-area',
    role: 'img',
    aria: { label: aria },
    tabindex: '0',
  });

  const tooltip = h('div', {
    class: 'chart-tooltip',
    role: 'status',
    style: [
      'position: absolute', 'pointer-events: none', 'background: var(--bg-surface)',
      'border: 1px solid var(--border-default)', 'border-radius: var(--radius-sm)',
      'padding: 6px 10px', 'font-size: 12px', 'box-shadow: var(--shadow-xs)',
      'white-space: nowrap', 'display: none', 'z-index: 2',
    ].join(';'),
  });
  (chartArea as HTMLElement).appendChild(tooltip);

  const eventsList = h('div', {
    class: 'chart-events',
    style: 'display: flex; flex-direction: column; gap: 4px; margin-top: 12px; font-size: 12px; color: var(--text-secondary);',
  });
  const evts = (props.events ?? []).slice().sort((a, b) =>
    new Date(a.started_at).getTime() - new Date(b.started_at).getTime());
  const eventRow = (ev: EventOut) => h('div', { class: 'chart-event-row' },
    `${eventIcon(ev.kind)} ${formatDate(ev.started_at)} · ${ev.title}`);
  // Список изменений в приёме дорос до десятков строк и оттеснял таблицу вниз.
  // Свежие три остаются на виду, история сворачивается — как «История дозировок»
  // на карточке препарата. Порядок внутри — по возрастанию даты, поэтому
  // свёрнутое (старое) стоит над видимым (свежим).
  const VISIBLE_EVENTS = 3;
  const older = evts.slice(0, Math.max(0, evts.length - VISIBLE_EVENTS));
  if (older.length) {
    eventsList.appendChild(h('details', { class: 'chart-events-older' }, [
      h('summary', { class: 'chart-events-summary' }, [
        h('span', { class: 'chart-events-chevron', 'aria-hidden': 'true' }, '▸'),
        h('span', null,
          `Ещё ${older.length} ${plural(older.length, ['изменение', 'изменения', 'изменений'])} раньше`),
      ]),
      h('div', { class: 'chart-events-older-body' }, older.map(eventRow)),
    ]));
  }
  for (const ev of evts.slice(older.length)) {
    eventsList.appendChild(eventRow(ev));
  }

  const wrapper = h('div', { class: 'trend-chart' }, [
    chartArea,
    evts.length > 0 ? eventsList : null,
    h('div', { class: 'chart-legend' }, [
      h('span', null, [
        h('span', { style: 'display:inline-block;width:10px;height:10px;border-radius:50%;background:var(--chart-point-above);vertical-align:middle;' }),
        ' Выше нормы',
      ]),
      h('span', null, [
        h('span', { style: 'display:inline-block;width:10px;height:10px;border-radius:50%;background:var(--chart-point-below);vertical-align:middle;' }),
        ' Ниже нормы',
      ]),
      h('span', null, [
        h('span', { style: 'display:inline-block;width:10px;height:10px;border-radius:50%;border:2px solid var(--chart-point-default);background:var(--bg-surface);vertical-align:middle;' }),
        ' В норме',
      ]),
      range.low != null && range.high != null ? h('span', null, [
        h('span', { style: 'display:inline-block;width:20px;height:10px;background:var(--chart-reference-band);border-top:1px dashed var(--chart-reference-border);border-bottom:1px dashed var(--chart-reference-border);vertical-align:middle;' }),
        ' Референс ',
        h('span', { class: 'tabular' }, `${formatLabValue(range.low)}–${formatLabValue(range.high)} ${unit}`),
      ]) : null,
    ]),
  ]);

  queueMicrotask(() => { void mountChart(); });

  let plot: uPlot | null = null;
  let selectedIdx = -1;

  function moveSelection(delta: number): void {
    if (!plot || !props.points.length) return;
    selectedIdx = Math.max(0, Math.min(props.points.length - 1, selectedIdx + delta));
    plot.setCursor({ left: plot.valToPos(plot.data[0]![selectedIdx]! as number, 'x'), top: 0 });
    const p = props.points[selectedIdx]!;
    if (p.value != null && plot) {
      showTooltip(selectedIdx, p);
    }
    if (props.onPointSelect) props.onPointSelect(p);
  }

  function showTooltip(idx: number, p: TrendPoint): void {
    if (!plot) return;
    const prev = idx > 0 ? props.points[idx - 1]! : null;
    tooltip.textContent = tooltipText(p, prev);
    const x = plot.valToPos(plot.data[0]![idx]! as number, 'x');
    const y = p.value != null ? plot.valToPos(p.value, 'y') : 20;
    (tooltip as HTMLElement).style.left = `${Math.max(8, Math.min(plot.over.clientWidth - 140, x - 70))}px`;
    (tooltip as HTMLElement).style.top = `${Math.max(0, y - 36)}px`;
    (tooltip as HTMLElement).style.display = 'block';
  }

  function hideTooltip(): void {
    (tooltip as HTMLElement).style.display = 'none';
  }

  const onKeydown = (ev: KeyboardEvent): void => {
    if (ev.key === 'ArrowRight') { ev.preventDefault(); moveSelection(+1); }
    else if (ev.key === 'ArrowLeft') { ev.preventDefault(); moveSelection(-1); }
    else if (ev.key === 'Home') { ev.preventDefault(); selectedIdx = -1; moveSelection(+1); }
    else if (ev.key === 'End') { ev.preventDefault(); selectedIdx = props.points.length; moveSelection(-1); }
    else if (ev.key === 'Escape') { hideTooltip(); }
  };
  (chartArea as HTMLElement).addEventListener('keydown', onKeydown);

  let tooltipHideTimer: ReturnType<typeof setTimeout> | null = null;

  const onTouchStart = (ev: TouchEvent): void => {
    if (!plot || !props.points.length) return;
    const touch = ev.touches[0];
    if (!touch) return;
    const rect = (chartArea as HTMLElement).getBoundingClientRect();
    const clientX = touch.clientX - rect.left;
    const clientY = touch.clientY - rect.top;
    plot.setCursor({ left: clientX, top: clientY });
    if (tooltipHideTimer != null) { clearTimeout(tooltipHideTimer); tooltipHideTimer = null; }
    tooltipHideTimer = setTimeout(() => { hideTooltip(); tooltipHideTimer = null; }, 3000);
  };

  (chartArea as HTMLElement).addEventListener('touchstart', onTouchStart, { passive: true });

  registerCleanup(() => {
    (chartArea as HTMLElement).removeEventListener('keydown', onKeydown);
    (chartArea as HTMLElement).removeEventListener('touchstart', onTouchStart);
    if (tooltipHideTimer != null) clearTimeout(tooltipHideTimer);
  });

  async function mountChart(): Promise<void> {
    if (!(chartArea as HTMLElement).isConnected) {
      queueMicrotask(() => { void mountChart(); });
      return;
    }
    if (!props.points.length) {
      (chartArea as HTMLElement).appendChild(h('div', {
        class: 'empty-state',
        role: 'status',
        style: 'padding-top: 40px;',
      }, 'Нет данных для графика. Загрузите первый анализ.'));
      return;
    }
    const uPlot = await loadUPlot();
    if (!(chartArea as HTMLElement).isConnected) return;

    // parseDate, а не new Date: date-only ISO по спецификации парсится как
    // UTC-полночь, а uPlot рисует ось в локальном времени — точка забора
    // западнее Гринвича вставала бы на предыдущий день (см. format.ts).
    // `?? NaN`, а не `!`: на нераспарсенной дате прежний `new Date(...)` давал
    // Invalid Date → NaN, точка просто не рисовалась. Assertion уронил бы весь график.
    const xs = props.points.map((p) => (parseDate(p.drawn_at)?.getTime() ?? NaN) / 1000);
    const ys = props.points.map((p) => p.value);
    const data: uPlot.AlignedData = [xs, ys];

    const width = (chartArea as HTMLElement).clientWidth || 600;
    const height = (chartArea as HTMLElement).clientHeight || 300;

    const opts: uPlot.Options = {
      width,
      height,
      padding: [12, 12, 8, 48],
      legend: { show: false },
      cursor: {
        show: true,
        points: { show: false },
        focus: { prox: 30 },
      },
      scales: {
        x: { time: true },
        y: {
          auto: true,
          range: (_u, min, max) => {
            let lo = min;
            let hi = max;
            if (range.low != null) lo = Math.min(lo, range.low);
            if (range.high != null) hi = Math.max(hi, range.high);
            const pad = (hi - lo) * 0.1 || 1;
            return [lo - pad, hi + pad];
          },
        },
      },
      axes: [
        {
          stroke: getVar('--chart-axis'),
          grid: { stroke: getVar('--chart-grid'), width: 1 },
        },
        {
          label: unit,
          labelSize: 12,
          stroke: getVar('--chart-axis'),
          grid: { stroke: getVar('--chart-grid'), width: 1 },
          size: 48,
        },
      ],
      series: [
        {},
        {
          label: props.marker.display_ru,
          stroke: getVar('--chart-line-primary'),
          width: 1.5,
          points: { show: false },
          value: (_u, v) => (v == null ? '—' : `${formatLabValue(v)} ${unit}`),
        },
      ],
      hooks: {
        draw: [bandHook(range), pointsHook(props.points)],
        setCursor: [
          (u) => {
            const idx = u.cursor.idx ?? null;
            if (idx == null) {
              hideTooltip();
              return;
            }
            const p = props.points[idx];
            if (p && p.value != null) {
              showTooltip(idx, p);
            } else {
              hideTooltip();
            }
          },
        ],
      },
    };

    plot = new uPlot(opts, data, chartArea as HTMLElement);

    const resize = () => {
      if (!plot) return;
      plot.setSize({ width: (chartArea as HTMLElement).clientWidth, height: (chartArea as HTMLElement).clientHeight });
    };
    const ro = new ResizeObserver(resize);
    ro.observe(chartArea as HTMLElement);
    registerCleanup(() => {
      ro.disconnect();
      plot?.destroy();
      plot = null;
    });
  }

  return wrapper;
}
